import { createNestedObject } from '@/lib/helpers/utils';
import { size } from 'lodash';
import each from 'lodash/each';
import isArray from 'lodash/isArray';
import isBoolean from 'lodash/isBoolean';
import isEmpty from 'lodash/isEmpty';
import memoize from 'lodash/memoize';

export type Profile = {
  host: string;
  fieldGroups: {
    [key: string]: FieldGroup;
  };
};

export type FieldGroup = {
  fields: {
    [key: string]: FieldInfo;
  };
  description: string;
  name: string;
  label: string;
};

export type FieldInfo = {
  type: string;
  name: string;
  label: string;
  cardinality: number;
  heading: string;
  weight: number;
  description: string;
  fieldGroup: string;
  options: FieldOptions[];
  fieldDependees: FieldDependency[];
  fieldDependents: FieldDependency[];
  otherRefTerm: string;
  required: boolean;
  profileCompletionField: boolean;
};

export type FieldDependency = {
  id: number;
  values: string[];
  description: string;
  fieldDependentId: number;
  fieldDependentName: string;
  fieldDependentGroups: string[];
  fieldDependeeId: number;
  fieldDependeeName: string;
  fieldDependeeGroups: string[];
};

export type FieldOptions = {
  id: string;
  label: string;
  children?: FieldOptions;
};

export type FieldValue = {
  label: string;
  value: string;
  otherValue?: string;
};

type RefDataValue = {
  otherValue: string;
  value: string;
};

export type User = {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  profileCompletion: number;
  fieldGroups: { [key: string]: { [key: string]: FieldValue } };
};

/**
 * Format users RefData values into what a components expects
 *
 * @param {FieldData} fieldValue
 * @param {FieldInfo} fieldInfo
 * @return {string[]}
 */
export const getFormattedRefDataValues = (fieldValue: FieldData, fieldInfo: FieldInfo) => {
  if (fieldInfo.cardinality === 1) {
    return fieldValue;
  }

  return fieldValue.rawValue.map((value: any) => {
    return value.value;
  });
};

/**
 * Returns label for value.
 *
 * @param {string} value
 * @param {FieldOptions[]} options
 * @return {string}
 */
export const getLabel = (value: string, options: FieldOptions[]) => {
  const findMatchingObject = (entireObj: any, keyToFind: any, valToFind: any) => {
    let foundObj;
    JSON.stringify(entireObj, (_, nestedValue) => {
      if (nestedValue && nestedValue[keyToFind] === valToFind) {
        foundObj = nestedValue;
      }
      return nestedValue;
    });
    return foundObj;
  };

  const option: FieldOptions = findMatchingObject(options, 'id', value) || ({} as any);
  if (option && option.label) {
    return option.label;
  }

  return '';
};

/**
 * Handles returning a display values.
 *
 * @param {any} field
 * @param {Profile} profileDetails
 * @param {string} key
 * @param {string} property
 *
 * @return {string}
 */
export const displayField = (field: any, profileDetails: Profile, key: string, property: string) => {
  if (isBoolean(field.value)) {
    if (field.value) {
      return 'Yes';
    } else {
      return 'No';
    }
  }

  if (isArray(field.value)) {
    const display: Array<string> = [];
    each(field.value, (value) => {
      if (value == null) {
        console.error(field);
      }
      const label = getLabel(value ? value.value : '', profileDetails.fieldGroups[key].fields[property]?.options);
      if (!isEmpty(label)) {
        // Only add to display if item exists in options list.
        display.push(label);
      }
    });
    return display.join(', ');
  }
  try {
    if (profileDetails.fieldGroups[key].fields[property].options) {
      return getLabel(field.value, profileDetails.fieldGroups[key].fields[property].options);
    } else {
      return field.value;
    }
  } catch (error) {
    console.error('Problem reading', key, property);
    return '';
  }
};

export type FieldData = {
  [key: string]: {
    label: string;
    value: string;
    rawValue: string | RefDataValue | string[] | RefDataValue[];
    otherValue?: string;
  };
};

/**
 * Returns an object of all available fields with their label and values.
 *
 * @param {User} user
 * @param {Profile} profileDetails
 * @return {FieldData}
 */
export const getProfileData = memoize((user: User, profileDetails: Profile) => {
  const fieldData: FieldData = {};
  if (!isEmpty(user) && !isEmpty(user.fieldGroups) && !isEmpty(profileDetails)) {
    for (const [key, fields] of Object.entries(user.fieldGroups)) {
      for (const [property, fieldValue] of Object.entries(fields)) {
        fieldData[property] = {
          label: fieldValue.label,
          value: displayField(fieldValue, profileDetails, key, property),
          rawValue: fieldValue.value
        };
        if (fieldValue.otherValue) {
          fieldData[property]['otherValue'] = fieldValue.otherValue;
        }
      }
    }
  }
  return fieldData;
});

/**
 * Given the name of a field, a user object and the profile details object
 *
 * It returns the label and value for a field
 *
 * @param {User} user
 * @param {Profile} profileDetails
 * @return {FieldValue | null}
 */
export const getUserFieldValue = memoize(
  (field: string, user: User, profileDetails: Profile): FieldValue | null => {
    if (isEmpty(user) || isEmpty(profileDetails)) {
      return null;
    }
    const fieldData: FieldData = getProfileData(user, profileDetails);
    if (field == 'name') {
      return { label: 'Full Name', value: user.firstName + ' ' + user.lastName };
    }
    if (field == 'email') {
      return { label: 'Email', value: user.email };
    }
    if (!isEmpty(fieldData[field])) {
      return { label: fieldData[field].label, value: fieldData[field].value };
    }
    return null;
  },
  (field, user, profileDetails) => {
    const cacheKey = {
      field: field,
      user: user,
      profileDetails: profileDetails
    };
    return JSON.stringify(cacheKey);
  }
);

export type GroupField = {
  group: string;
  field: string;
  description?: string;
  groupDescription?: string;
  heading?: string;
  label?: string;
  weight?: number;
  options?: FieldOptions[];
  otherRefTerm?: string;
  type?: string;
  fieldDependents?: any;
};

const generateProfileObject = (
  firstGroupItem: boolean,
  key: string,
  info: FieldInfo,
  groupDescription: string
): GroupField => {
  const description = firstGroupItem ? groupDescription : undefined;
  return {
    group: key,
    field: info.name,
    otherRefTerm: info.otherRefTerm,
    groupDescription: description,
    description: info.description,
    heading: info.heading,
    label: info.label,
    weight: info.weight,
    type: info.type,
    options: info.options,
    fieldDependents: info?.fieldDependents ?? []
  };
};

interface NewUser {
  [group: string]: {
    [field: string]: any;
  };
}

/**
 * Builds custom profile object from a flat list of values from a form
 *
 * @param {any} values
 * @param {User} profile
 * @return {any}
 */
export const buildUserWithValuesFromFlatList = (values: any, profile: Profile) => {
  const newUser: NewUser = {};

  for (const [group, groupData] of Object.entries(profile.fieldGroups)) {
    for (const [field] of Object.entries(groupData.fields)) {
      // if field in values then replace field in user
      if (Object.keys(values).includes(field)) {
        createNestedObject(newUser, [group, field]);
        // mismatch between submission and form expectation. Submission wants 1/0 form wants true/false
        newUser[group][field] = values[field] === 'true' ? 1 : values[field] === 'false' ? 0 : values[field];
        if (values[field + '_other']) {
          newUser[group][field] = {
            value: values[field],
            otherValue: values[field + '_other']
          };
        }
      }
    }
  }

  return {
    profile: newUser
  };
};

/**
 * Finds the object with the highest weight for each unique fieldGroup in the given array of FieldInfo objects.
 * @param fieldGroupObjects An array of FieldInfo objects with fieldGroup property.
 * @return An object with the highest weight for each unique fieldGroup.
 */
function getLowestWeightByFieldGroup(fieldGroupObjects: FieldInfo[]) {
  const weightsByGroup: { [key: string]: any } = {};

  fieldGroupObjects.forEach((obj) => {
    // Get the current weight, defaulting to Infinity if weight is undefined
    const currentWeight = obj.weight !== undefined ? obj.weight : Infinity;

    // Check if the fieldGroup is not in weightsByGroup or if the currentWeight is lower
    if (!(obj.fieldGroup in weightsByGroup) || currentWeight < weightsByGroup[obj.fieldGroup].lowestWeight) {
      // Update weightsByGroup with the new lowestWeight
      weightsByGroup[obj.fieldGroup] = {
        lowestWeight: currentWeight,
        fieldName: obj.name,
        fieldObject: obj
      };
    }
  });

  return weightsByGroup;
}

/**
 * Returns an array of empty fields.
 *
 * @param {User} user
 * @param {Profile} profile
 * @param {Array<string>} fieldsWeCareAbout
 * @param {boolean} fullProfile
 * @param {boolean} sectionBased
 *
 * @return {GroupField[] | null}
 */
export const getEmptyFieldDetails = (
  user: User,
  profile: Profile,
  fieldsWeCareAbout: Array<string>
): GroupField[] | null => {
  const emptyFields = [];
  if (isEmpty(profile.fieldGroups)) {
    return null;
  }

  for (const [key, fieldGroup] of Object.entries(profile.fieldGroups)) {
    for (const [, fieldInfo] of Object.entries(fieldGroup.fields)) {
      const fieldInfoWithGroup = { ...fieldInfo, fieldGroup: key };
      try {
        if (fieldsWeCareAbout.includes(fieldInfoWithGroup.name)) {
          if (!isEmpty(user)) {
            if (size(fieldInfoWithGroup.fieldDependees) > 0) {
              // If parent dependent value not correct, ignore this field
              const parent = fieldInfoWithGroup.fieldDependees[0].fieldDependeeName;
              if (
                !fieldInfoWithGroup.fieldDependees[0].values.includes(String(user.fieldGroups?.[key]?.[parent]?.value))
              ) {
                continue;
              }
            }
            if (fieldInfoWithGroup.type == 'Boolean') {
              if (isEmpty(user.fieldGroups[key]) || user.fieldGroups[key][fieldInfoWithGroup.name]?.value == null) {
                emptyFields.push(fieldInfoWithGroup);
              }
            } else {
              if (
                isEmpty(user.fieldGroups[key]) ||
                isEmpty(user.fieldGroups[key][fieldInfoWithGroup.name]) ||
                isEmpty(user.fieldGroups[key][fieldInfoWithGroup.name].value)
              ) {
                emptyFields.push(fieldInfoWithGroup);
              }
            }
          } else {
            emptyFields.push(fieldInfoWithGroup);
          }
        }
      } catch (error) {
        console.error(error);
      }
    }
  }

  const firstGroupItems = getLowestWeightByFieldGroup(emptyFields);

  // transform the empty fields and make sure the first of each group has the group description
  const profileFieldDetails = emptyFields.map((field) => {
    const firstGroupItem = firstGroupItems[field.fieldGroup].fieldName === field.name;
    const groupDescription = profile.fieldGroups[field.fieldGroup].description;
    return generateProfileObject(firstGroupItem, field.fieldGroup, field, groupDescription);
  });

  return profileFieldDetails;
};

/**
 * Get profile completion fields based off user service profile config.
 *
 * @param {Profile} profileDetails
 *
 * @return{[]}
 *  Array of user field names.
 */
export const getProfileCompletionFields = memoize((profileDetails: Profile) => {
  const data = [];
  if (profileDetails) {
    for (const [group, groupData] of Object.entries(profileDetails.fieldGroups)) {
      for (const [field, fieldData] of Object.entries(groupData.fields)) {
        // Ensure the field has profileCompletion flag.
        if (fieldData.profileCompletionField) {
          data.push(field);
        }
      }
    }
  }
  return data;
});
